import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIsEmbeddedMobile } from '../../../_hooks/useIsEmbeddedMobile';
import { SegmentedControl } from '../../../CommonComponents/OPL/SegmentedControl/SegmentedControl';
import { SegmentedControlLoading } from '../../../CommonComponents/OPL/SegmentedControl/SegmentedControl.loading';
import { SegmentedControlShell } from '../../../CommonComponents/OPL/SegmentedControl/SegmentedControl.styles';
import { TabSlug } from '../../types';
import { useGroupSelection } from './useGroupSelection';

interface Props {
  id: string;
  tabSlug: TabSlug;
}

export const GroupSelection = ({ id, tabSlug }: Props) => {
  const isEmbeddedMobile = useIsEmbeddedMobile();

  if (isEmbeddedMobile) {
    return null;
  }

  return (
    <GroupSelectionRenderer id={id}>
      <GroupSelectionContent id={id} tabSlug={tabSlug} />
    </GroupSelectionRenderer>
  );
};

interface GroupSelectionRendererProps extends Omit<Props, 'tabSlug'> {
  children?: React.ReactNode;
}

export const GroupSelectionRenderer = ({ id, children }: GroupSelectionRendererProps) => {
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const element = document.getElementById(`group-selection--portal-target--${id}`);

    if (element) {
      setPortalTarget(element);
    }
  }, [id]);

  if (!portalTarget) {
    return null;
  }

  return createPortal(children, portalTarget);
};

const GroupSelectionContent = ({ id, tabSlug }: Props) => {
  const {
    data: { isLoading, tabList, defaultTabIndex, selectedTabIndex, isHidden },
    actions: { onTabChange },
  } = useGroupSelection({ id, tabSlug });

  if (isHidden) {
    return null;
  }

  if (isLoading) {
    return <SegmentedControlLoading mt="16" />;
  }

  if (!tabList) {
    return <SegmentedControlShell mt="16" />;
  }

  return (
    <SegmentedControl
      my="16"
      id={`${id}-drill-down-sc`}
      values={tabList}
      selectedIndex={selectedTabIndex ?? defaultTabIndex ?? 0}
      onChange={onTabChange}
      ariaLabel="Select group"
    />
  );
};
